import type { CustomInbox } from '@/features/customInbox/types/CustomInbox';
import type { EDSActiveFilter, EDSFilterPrimitiveValue } from '@/features/eds/types/EDSFilterConfig';
import type { EDSFiltersRequestFiltersList } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';
import type { EdsSortingRequest } from '@/features/eds/types/api/requests/EdsSortingRequestBody';
import type { ControlledTabListTab } from '@/components/ui/lists/InteractiveList/components/ControlledTabList/ControlledTabList';
import { SortDirection } from '@/enums/SortDirection';
import TypographyService from '@/services/TypographyService/TypographyService';

export default class CustomInboxService {
  public static prepareFilterKeys(filters?: EDSFiltersRequestFiltersList): string[] {
    return filters?.map(({ propertyName }) => propertyName) || [];
  }

  public static prepareCustomInboxCode<T extends string>(inboxName: string): T {
    return inboxName.replace(/\s/g, '') as T;
  }

  public static prepareCustomInboxName(inboxName: string): string {
    return TypographyService.capitalize(inboxName);
  }

  public static transformCustomInboxesToTabs<T extends string>(
    customInboxes: CustomInbox[],
  ): ControlledTabListTab<T>[] {
    return customInboxes.map((customInbox) => ({
      label: this.prepareCustomInboxName(customInbox.name),
      value: this.prepareCustomInboxCode<T>(customInbox.name),
      counter: customInbox.counter || 0,
      isDeletable: true,
      payload: {
        id: customInbox.id,
        entityCode: customInbox.entityName,
        location: customInbox.location,
        filters: customInbox?.filters,
        sorting: customInbox?.sorting,
      },
    }));
  }

  public static transformActiveFiltersForSaving(filters: EDSActiveFilter[]): {
    filters: EDSFiltersRequestFiltersList;
    sorting: EdsSortingRequest;
  } {
    const convertedActiveFilters: any = filters.reduce(
      (acc, filter) => {
        if (filter?.isSorting)
          return { ...acc, sorting: { propertyName: filter.key, sortDirection: filter.value as SortDirection } };

        return {
          ...acc,
          filters: {
            ...acc.filters,
            [filter.key]: [...(acc.filters?.[filter.key] || []), filter.value as EDSFilterPrimitiveValue],
          },
        };
      },
      { filters: {}, sorting: null } as {
        filters: Record<string, EDSFilterPrimitiveValue[]>;
        sorting: EdsSortingRequest;
      },
    );

    return {
      filters: Object.keys(convertedActiveFilters.filters).map((propertyName) => ({
        propertyName,
        values: convertedActiveFilters.filters[propertyName],
      })),
      sorting: convertedActiveFilters.sorting,
    };
  }

  public static normalizeResponseRecord(data: Record<string, CustomInbox>) {
    return Object.keys(data).reduce(
      (acc, key) => ({ ...acc, [`${key[0].toUpperCase()}${key.slice(1)}`]: data[key] }),
      {},
    );
  }
}
